import React from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { edgeOnboardApi, locationsApi } from '../../api/services';
import type {
  EdgeOnboardCustomerUser,
  EdgeOnboardGoLive,
  EdgeOnboardGoLiveState,
  Location,
} from '../../api/config';
import { extractApiError } from '../../utils/apiErrors';
import {
  EoEmpty,
  EoIcon,
  EoPage,
  EoPageHeader,
  formatEoDate,
  GoLiveStateChip,
  ReviewStateChip,
  useEoSnackbar,
} from './eoShared';

const GO_LIVE_STATES: EdgeOnboardGoLiveState[] = ['Not Started', 'In Progress', 'Complete'];

// One go-live per location at a time; the location name opens the detail page.
export const GoLivesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { showSuccess, snackbarElement } = useEoSnackbar();

  const navigate = useNavigate();
  // ?create=1 deep link (e.g. from the Users page's "Create go-live" action)
  // lands with the create dialog already open.
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = React.useState('');
  const [stateFilter, setStateFilter] = React.useState<EdgeOnboardGoLiveState | null>(null);
  const [stateMenuAnchor, setStateMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [createOpen, setCreateOpen] = React.useState(() => searchParams.get('create') === '1');

  React.useEffect(() => {
    if (searchParams.get('create') === '1') {
      // Consume the param so refresh/back doesn't re-open the dialog.
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goLivesQuery = useQuery({
    queryKey: ['edge-onboard', 'go-lives'],
    queryFn: () => edgeOnboardApi.goLives.getAll(),
  });
  const locationsQuery = useQuery({
    queryKey: ['edge-onboard', 'locations-all'],
    queryFn: () => locationsApi.getAll(),
  });
  // include_deleted so a go-live assigned to a since-deleted user still shows
  // who it belongs to (marked "(deleted)") instead of a blank cell.
  const usersQuery = useQuery({
    queryKey: ['edge-onboard', 'customer-users', 'all'],
    queryFn: () => edgeOnboardApi.customerUsers.getAll({ include_deleted: true }),
  });

  const locationsById = React.useMemo(() => {
    const map = new Map<number, Location>();
    (locationsQuery.data ?? []).forEach(l => map.set(l.id, l));
    return map;
  }, [locationsQuery.data]);

  const usersById = React.useMemo(() => {
    const map = new Map<number, EdgeOnboardCustomerUser>();
    (usersQuery.data ?? []).forEach(u => map.set(u.id, u));
    return map;
  }, [usersQuery.data]);

  const rows = React.useMemo(() => {
    let list = goLivesQuery.data ?? [];
    if (stateFilter) list = list.filter(g => g.state === stateFilter);
    const term = search.trim().toLowerCase();
    if (term) {
      list = list.filter(g => {
        const loc = locationsById.get(g.location_id);
        const user = usersById.get(g.customer_user_id);
        return (
          loc?.name.toLowerCase().includes(term) ||
          (user?.email ?? '').toLowerCase().includes(term) ||
          (user?.username ?? '').toLowerCase().includes(term) ||
          `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.toLowerCase().includes(term)
        );
      });
    }
    return list;
  }, [goLivesQuery.data, stateFilter, search, locationsById, usersById]);

  const isLoading = goLivesQuery.isLoading || locationsQuery.isLoading || usersQuery.isLoading;
  const loadError = goLivesQuery.error || locationsQuery.error || usersQuery.error;

  return (
    <EoPage>
      <EoPageHeader
        title="Go-Lives"
        sub="Scheduled installs. One per location, assigned to a customer user."
        action={
          <button className="ea-btn contained" onClick={() => setCreateOpen(true)}>
            <EoIcon name="add" />
            Create Go-Live
          </button>
        }
      />

      <div className="ea-filter-bar">
        <div className="ea-filter wide">
          <EoIcon name="search" />
          <input
            placeholder="Search location or user…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            aria-label="Search go-lives"
          />
        </div>
        <button
          className={`ea-filter${stateFilter ? ' has-value' : ''}`}
          onClick={e => setStateMenuAnchor(e.currentTarget)}
          aria-haspopup="menu"
        >
          {stateFilter ? (
            <>
              <span className="label-prefix">State:</span>
              {stateFilter}
              <span
                className="ea-ico clear"
                role="button"
                aria-label="Clear state filter"
                onClick={e => {
                  e.stopPropagation();
                  setStateFilter(null);
                }}
              >
                close
              </span>
            </>
          ) : (
            <>
              <EoIcon name="filter_list" />
              State
              <EoIcon name="expand_more" />
            </>
          )}
        </button>
        <Menu
          anchorEl={stateMenuAnchor}
          open={Boolean(stateMenuAnchor)}
          onClose={() => setStateMenuAnchor(null)}
        >
          {GO_LIVE_STATES.map(s => (
            <MenuItem
              key={s}
              selected={s === stateFilter}
              onClick={() => {
                setStateFilter(s);
                setStateMenuAnchor(null);
              }}
            >
              {s}
            </MenuItem>
          ))}
        </Menu>
        <div className="spacer" />
        <span className="results">
          <strong>{rows.length}</strong> go-live{rows.length === 1 ? '' : 's'}
        </span>
      </div>

      {loadError ? (
        <Alert severity="error">{extractApiError(loadError, 'Failed to load go-lives')}</Alert>
      ) : isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <div className="ea-paper" style={{ overflow: 'hidden' }}>
          {rows.length === 0 ? (
            <EoEmpty
              icon="bolt"
              message={
                goLivesQuery.data?.length
                  ? 'No go-lives match the current filters.'
                  : 'No go-lives yet — create one to start an onboarding.'
              }
            />
          ) : (
            <div className="eo-table-wrap">
            <table className="eo-table">
              <thead>
                <tr>
                  <th>Location</th>
                  <th>Customer User</th>
                  <th>State</th>
                  <th>Review</th>
                  <th style={{ textAlign: 'center' }}>Subs</th>
                  <th style={{ textAlign: 'center' }}>Slips</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(g => {
                  const loc = locationsById.get(g.location_id);
                  const user = usersById.get(g.customer_user_id);
                  return (
                    <tr key={g.id}>
                      <td>
                        <RouterLink className="edge-link with-arrow" to={`/onboarding/go_lives/${g.id}`}>
                          <span className="edge-link__text">{loc?.name ?? `Location #${g.location_id}`}</span>
                          <span className="edge-link__arrow">arrow_forward</span>
                        </RouterLink>
                        <div className="muted mono" style={{ fontSize: 11, marginTop: 2 }}>
                          {loc?.hexahq_account_id ? `HQ #${loc.hexahq_account_id}` : '—'}
                        </div>
                      </td>
                      <td>
                        {user ? (
                          <>
                            {user.first_name} {user.last_name ?? ''}{' '}
                            {user.deleted_at && <span className="muted">(deleted) </span>}
                            <span className="muted mono">· {user.email ?? user.username}</span>
                          </>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td><GoLiveStateChip state={g.state} /></td>
                      <td><ReviewStateChip state={g.review_state} /></td>
                      <td style={{ textAlign: 'center' }}>{g.counts?.submissions ?? '—'}</td>
                      <td style={{ textAlign: 'center' }}>{g.counts?.tag_slips_total ?? '—'}</td>
                      <td className="muted">{formatEoDate(g.created_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
        </div>
      )}

      <CreateGoLiveDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        locations={locationsQuery.data ?? []}
        users={usersQuery.data ?? []}
        goLives={goLivesQuery.data ?? []}
        onCreated={created => {
          setCreateOpen(false);
          // Next step in the setup flow is loading tag slips — hand them a
          // one-click jump with the go-live preselected.
          showSuccess('Go-live created', {
            label: 'Add tag slips',
            onClick: () => navigate(`/onboarding/tag_slips?go_live_id=${created.id}`),
          });
          void queryClient.invalidateQueries({ queryKey: ['edge-onboard', 'go-lives'] });
        }}
      />
      {snackbarElement}
    </EoPage>
  );
};

interface CreateGoLiveDialogProps {
  open: boolean;
  onClose: () => void;
  locations: Location[];
  users: EdgeOnboardCustomerUser[];
  goLives: EdgeOnboardGoLive[];
  onCreated: (created: EdgeOnboardGoLive) => void;
}

export const CreateGoLiveDialog: React.FC<CreateGoLiveDialogProps> = ({
  open,
  onClose,
  locations,
  users,
  goLives,
  onCreated,
}) => {
  const [location, setLocation] = React.useState<Location | null>(null);
  const [user, setUser] = React.useState<EdgeOnboardCustomerUser | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  // Reset per open so a cancelled draft doesn't leak into the next create.
  React.useEffect(() => {
    if (open) {
      setLocation(null);
      setUser(null);
      setError(null);
    }
  }, [open]);

  const activeGoLiveLocationIds = React.useMemo(
    () => new Set(goLives.filter(g => g.state !== 'Complete').map(g => g.location_id)),
    [goLives],
  );

  // "Scoped to users at the selected location."
  const scopedUsers = React.useMemo(
    () => (location ? users.filter(u => u.location_id === location.id && !u.deleted_at) : []),
    [users, location],
  );

  const createMutation = useMutation({
    mutationFn: (data: { location_id: number; customer_user_id: number }) =>
      edgeOnboardApi.goLives.create(data),
    onSuccess: created => onCreated(created),
    onError: err => setError(extractApiError(err, 'Failed to create go-live')),
  });

  const disabledReason = (l: Location): string | null => {
    if (!l.hexahq_account_id) return 'Link an HQ Account to enable';
    if (activeGoLiveLocationIds.has(l.id)) return 'An active go-live already exists here';
    return null;
  };

  return (
    // Dialogs portal to <body>, outside the page's .eo-scope wrapper — the
    // paper re-applies the scope class so ea-* styles work inside.
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ className: 'eo-scope' }}>
      <DialogTitle sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 600 }}>
        Create Go-Live
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.25, pt: '8px !important' }}>
        {error && <Alert severity="error">{error}</Alert>}
        <Autocomplete
          options={locations}
          value={location}
          onChange={(_, value) => {
            setLocation(value);
            setUser(null);
          }}
          getOptionLabel={l => l.name}
          getOptionDisabled={l => disabledReason(l) !== null}
          renderOption={(props, l) => {
            const reason = disabledReason(l);
            return (
              <Tooltip key={l.id} title={reason ?? ''} placement="right">
                {/* span keeps the tooltip live on disabled options */}
                <span>
                  <li {...props}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <Box sx={{ flex: 1 }}>{l.name}</Box>
                      {l.hexahq_account_id ? (
                        <Box component="span" sx={{ color: 'text.secondary', fontSize: 12, fontFamily: 'monospace' }}>
                          HQ #{l.hexahq_account_id}
                        </Box>
                      ) : (
                        <Box component="span" sx={{ color: 'warning.main', fontSize: 12 }}>
                          Not linked
                        </Box>
                      )}
                    </Box>
                  </li>
                </span>
              </Tooltip>
            );
          }}
          renderInput={params => (
            <TextField
              {...params}
              label="Location"
              required
              helperText="Unlinked locations are disabled — link an HQ Account from the Locations page first."
            />
          )}
        />
        <Autocomplete
          options={scopedUsers}
          value={user}
          onChange={(_, value) => setUser(value)}
          disabled={!location}
          getOptionLabel={u => `${u.first_name} ${u.last_name ?? ''}${u.email || u.username ? ` (${u.email ?? u.username})` : ''}`.trim()}
          noOptionsText={location ? 'No customer users at this location — add one on the Users page.' : ''}
          renderInput={params => (
            <TextField
              {...params}
              label="Customer User"
              required
              helperText="Scoped to users at the selected location."
            />
          )}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <button className="ea-btn text" onClick={onClose} disabled={createMutation.isPending}>
          Cancel
        </button>
        <button
          className="ea-btn contained"
          disabled={!location || !user || createMutation.isPending}
          onClick={() => {
            if (!location || !user) return;
            setError(null);
            createMutation.mutate({ location_id: location.id, customer_user_id: user.id });
          }}
        >
          {createMutation.isPending ? 'Creating…' : 'Create Go-Live'}
        </button>
      </DialogActions>
    </Dialog>
  );
};
